/**
 * XR Subsystem — Types, configuration, and centralized session state.
 */

import type * as THREE from 'three/webgpu';

// ─── Configuration ──────────────────────────────────────────────────────────────

export type XRSessionModeString = 'immersive-vr' | 'immersive-ar' | 'inline';

export interface XRConfig {
  /** Default session mode when requestSession() is called without argument. */
  defaultMode: XRSessionModeString;
  /** Reference space type. Default: 'local-floor'. */
  referenceSpaceType: XRReferenceSpaceType;
  /** Features that must be present or session request fails. */
  requiredFeatures?: string[];
  /** Features requested but not mandatory (graceful degradation). */
  optionalFeatures?: string[];
  /** HTML element for AR DOM overlay. */
  domOverlayElement?: HTMLElement;
  /** Foveation level 0..1 (0=none, 1=max). Must be set before session. */
  foveation?: number;
  /** Framebuffer resolution scale. Must be set before session. Default: 1.0. */
  framebufferScaleFactor?: number;
  /** Enable experimental WebGPU XR binding (visionOS Safari 26.2+). Default: false. */
  allowWebGpuXr?: boolean;
  /**
   * Use the WebXR Layers API (XRProjectionLayer) when the browser supports
   * it. Default: true (three's default path). Set false to force the classic
   * XRWebGLLayer base-layer path — the simpler, wider-tested composition
   * route (no external-texture MSAA resolve) and the one the vr-sim emulator
   * validates. Useful when a device shows layer-composition artifacts
   * (misaligned/overlapping eyes) on the projection-layer path.
   */
  useLayersApi?: boolean;
  /**
   * Preferred display rate: 'max' picks the highest the session supports
   * (Quest 3: 120 Hz), a number picks the highest supported rate ≤ it.
   * Ignored where updateTargetFrameRate isn't implemented.
   */
  targetFrameRate?: number | 'max';
  /**
   * Depth-sensing session options. Default (AR): gpu-optimized first,
   * float32 first. Set explicitly for cpu-optimized (occlusion queries on
   * the CPU) or to change format preference.
   */
  depthSensing?: { usagePreference: string[]; dataFormatPreference: string[] };
}

export const DEFAULT_XR_CONFIG: XRConfig = {
  defaultMode: 'immersive-vr',
  referenceSpaceType: 'local-floor',
  requiredFeatures: ['local-floor'],
  optionalFeatures: ['hand-tracking'],
  foveation: 1,
  framebufferScaleFactor: 1.0,
  allowWebGpuXr: false,
};

// ─── Planes / Meshes ────────────────────────────────────────────────────────────

/** A tracked real-world plane (plane-detection). Pose is planeSpace→refSpace. */
export interface XRPlaneInfo {
  id: number;
  /** Column-major 4×4 pose matrix in the session reference space. */
  matrix: Float32Array;
  /** Boundary polygon in plane-local space (y≈0), [x, z] pairs. */
  polygon: Array<[number, number]>;
  orientation: 'horizontal' | 'vertical';
  /** Quest semantic label: wall/floor/ceiling/table/… ('' when absent). */
  semanticLabel: string;
  lastChangedTime: number;
}

/** A tracked real-world mesh chunk (mesh-detection, Quest scene mesh). */
export interface XRMeshInfo {
  id: number;
  /** Column-major 4×4 pose matrix in the session reference space. */
  matrix: Float32Array;
  vertices: Float32Array;
  indices: Uint32Array;
  semanticLabel: string;
  lastChangedTime: number;
}

// ─── Capabilities ───────────────────────────────────────────────────────────────

export interface XRCapabilities {
  readonly webxrAvailable: boolean;
  readonly vrSupported: boolean;
  readonly arSupported: boolean;
  readonly inlineSupported: boolean;
}

// ─── Session State ──────────────────────────────────────────────────────────────

/**
 * Centralized XR session state. Owned by XRModule, read by all XR systems.
 * Avoids scattered state across individual systems.
 */
export class XRSessionState {
  /** Currently active XR session, or null. */
  session: XRSession | null = null;
  /** Active reference space, set on sessionstart. */
  referenceSpace: XRReferenceSpace | null = null;
  /** Session mode string. */
  mode: XRSessionModeString | null = null;
  /** Features actually granted by the session. */
  private _enabledFeatures: readonly string[] = [];
  /**
   * Mirror of `enabledFeatures` for O(1) `hasFeature()` lookups. Multiple
   * XR systems (HitTest, Anchor, Depth, LightProbe) call hasFeature() on
   * their hot path every frame at 90+ Hz; an Array.includes per call adds
   * up to measurable overhead when features contain 10+ entries.
   */
  private _enabledFeaturesSet = new Set<string>();

  get enabledFeatures(): readonly string[] {
    return this._enabledFeatures;
  }
  set enabledFeatures(features: readonly string[]) {
    this._enabledFeatures = features;
    this._enabledFeaturesSet.clear();
    for (const f of features) this._enabledFeaturesSet.add(f);
  }
  /** Depth range of the active XR presentation backend. */
  depthRange: 'neg1to1' | '0to1' = 'neg1to1';

  // ─── Hit Test ─────────────────────────────────────────────────────

  /** Active hit test source (viewer space). */
  hitTestSource: XRHitTestSource | null = null;
  /** Last valid hit test pose matrix (column-major Float32Array(16)). */
  lastHitPoseMatrix: Float32Array | null = null;
  /** Whether a valid hit result was found this frame. */
  hasHitResult = false;

  // ─── Anchors ──────────────────────────────────────────────────────

  /** Map of tracked anchors → ECS entity IDs. */
  anchorEntityMap = new Map<XRAnchor, number>();
  /** Monotonic anchor counter for event IDs. */
  private _anchorIdCounter = 0;
  /** Map anchor → numeric ID for events. */
  anchorIdMap = new Map<XRAnchor, number>();

  nextAnchorId(): number {
    return this._anchorIdCounter++;
  }

  // ─── Planes / Meshes ──────────────────────────────────────────────

  /** Monotonic id counters for plane/mesh events. */
  private _planeIdCounter = 0;
  private _meshIdCounter = 0;
  /** XRPlane → numeric id (stable for the plane's lifetime). */
  planeIdMap = new Map<XRPlane, number>();
  /** Live detected planes by id (plane-detection feature). */
  detectedPlanes = new Map<number, XRPlaneInfo>();
  /** XRMesh → numeric id. */
  meshIdMap = new Map<XRMesh, number>();
  /** Live detected meshes by id (mesh-detection feature). */
  detectedMeshes = new Map<number, XRMeshInfo>();

  nextPlaneId(): number {
    return this._planeIdCounter++;
  }

  nextMeshId(): number {
    return this._meshIdCounter++;
  }

  // ─── Depth ────────────────────────────────────────────────────────

  /** CPU depth info for the current frame (if depth-sensing granted). */
  cpuDepthInfo: XRCPUDepthInformation | null = null;

  // ─── Light Estimation ─────────────────────────────────────────────

  /** Active light probe. */
  lightProbe: XRLightProbe | null = null;
  /** Current light estimate. */
  lightEstimate: XRLightEstimate | null = null;
  /** Managed directional light updated by light probe system. */
  managedLight: THREE.DirectionalLight | null = null;

  // ─── DOM Overlay ──────────────────────────────────────────────────

  /** Type of DOM overlay granted, or null. */
  domOverlayType: string | null = null;

  // ─── Controllers ──────────────────────────────────────────────────

  /** Controller ECS entity IDs by index. */
  controllerEntities = new Map<number, number>();
  /** Three.js controller groups by index. */
  controllerGroups = new Map<number, THREE.Group>();
  /** Three.js controller grip groups by index. */
  controllerGripGroups = new Map<number, THREE.Group>();
  /** Three.js hand groups by index. */
  handGroups = new Map<number, THREE.Group>();

  // ─── Helpers ──────────────────────────────────────────────────────

  get isPresenting(): boolean {
    return this.session !== null;
  }

  hasFeature(feature: string): boolean {
    return this._enabledFeaturesSet.has(feature);
  }

  /** Reset all state for session end. */
  reset(): void {
    this.session = null;
    this.referenceSpace = null;
    this.mode = null;
    this.enabledFeatures = [];
    this.depthRange = 'neg1to1';
    this.hitTestSource = null;
    this.lastHitPoseMatrix = null;
    this.hasHitResult = false;
    this.cpuDepthInfo = null;
    this.lightProbe = null;
    this.lightEstimate = null;
    this.domOverlayType = null;
    this._anchorIdCounter = 0;
    this._planeIdCounter = 0;
    this._meshIdCounter = 0;
    this.planeIdMap.clear();
    this.detectedPlanes.clear();
    this.meshIdMap.clear();
    this.detectedMeshes.clear();
    // Note: anchorEntityMap and controllerEntities are cleaned up
    // by their respective systems during session end.
  }
}
